<template>
    <div class="container">
        <div v-if="!isFormVisible" class="card">
            <div class="card-header d-flex justify-content-between align-items-center ps-4 pt-3 pb-4">
                <h5><i class="fa-duotone fa-solid fa-list"></i> Categorias</h5>
                <argon-button size="sm" color="primary" variant="gradient" @click="openCreateModal">
                    <span class="fa-solid fa-plus" />
                    Crear
                </argon-button>
            </div>
            <div class="card-body pt-0">
                <CustomFilters
:order-options="orderOptions" :items-per-page="itemsPerPage" :view-as-card="viewAsCard"
                    :show-deleted="showDeleted" @apply-filter="applyFilter" @update-items-per-page="updateItemsPerPage"
                    @update-view-as-card="updateViewAsCard" @update-show-deleted="updateShowDeleted" />
                <div v-if="loading" class="loading-indicator">
                    <p>Cargando datos...</p>
                </div>
                <div v-else>
                    <div v-if="filteredItems.length === 0" class="no-records p-5 px-4">
                        <p>No se encontraron registros. Intenta ajustar los filtros o añadir nuevos registros.</p>
                    </div>
                    <div v-else>
                        <div v-if="!viewAsCard" class="table-responsive">
                            <GenericTable
:data="filteredItems" :headers="headers" @delete-record="deleteItem"
                                @edit-record="openEditModal" @restore-record="restoreItem" />
                        </div>
                        <transition-group ref="listContainer" name="fade-move" tag="div" class="row me-2 ms-2 pt-2">
                            <template v-if="viewAsCard">
                                <GenericCard
v-for="(item) in filteredItems" :key="item.id" ref="listItems" :data="item"
                                    class="fade-card" @delete-record="deleteItem" @edit-record="openEditModal"
                                    @restore-record="restoreItem" />
                            </template>
                        </transition-group>
                        <Pagination
:total-items="totalFilteredItems" :current-page="currentPage" :last-page="lastPage"
                            :items-per-page="itemsPerPage" @page-change="changePage" />
                    </div>
                </div>
            </div>
        </div>
        <ModelModal
v-if="showModal && !displayAsForm" v-model="showModal" :is-edit-mode="isEditMode"
            :resource-api="resourceApi" :entity-data="entityData" :display-as-form="displayAsForm"
            :resource-name="resourceName" @entity-updated="handleEntityUpdated">
            <template #default="{ entity, errors }">
                <div class="mb-3">
                    <label for="name" class="form-label">Nombre</label>
                    <input
id="name" v-model="entity.name" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.name }">
                    <div v-if="errors.name" class="invalid-feedback">
                        {{ errors.name[0] }}
                    </div>
                </div>
            </template>
        </ModelModal>
        <div v-if="isFormVisible && displayAsForm" class="card">
            <div class="card-header d-flex justify-content-between align-items-center ps-4 pt-3 pb-4">
                <h5><i class="fa-duotone fa-solid fa-list"></i> {{ isEditMode ? 'Editar' : 'Crear' }} Aplicación</h5>
                <argon-button size="sm" color="primary" variant="gradient" @click="closeForm">
                    <span class="fa-solid fa-arrow-left" />
                    Volver
                </argon-button>
            </div>
            <div class="card-body pt-0">
                <ModelModal
v-model="showModal" :is-edit-mode="isEditMode" :resource-api="resourceApi"
                    :entity-data="entityData" :display-as-form="displayAsForm" :keep-form-open="keepFormOpen"
                    :resource-name="resourceName" @entity-updated="handleEntityUpdated">
                    <template #default="{ entity, errors }">
                        <div class="form-grid">
                            <div class="mb-3">
                                <label for="name" class="form-label">Nombre</label>
                                <input
id="name" v-model="entity.name" type="text" class="form-control"
                                    :class="{ 'is-invalid': errors.name }">
                                <div v-if="errors.name" class="invalid-feedback">
                                    {{ errors.name[0] }}
                                </div>
                            </div>
                            <div class="mb-3">
                                <label for="description" class="form-label">Descripción</label>
                                <input
id="description" v-model="entity.description" type="text" class="form-control"
                                    :class="{ 'is-invalid': errors.description }">
                                <div v-if="errors.description" class="invalid-feedback">
                                    {{ errors.description[0] }}
                                </div>
                            </div>
                        </div>
                    </template>
                </ModelModal>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';
import * as Choices from "choices.js";
import { useModelActions } from '@/composables/useModelActions';
import ModelModal from '@/components/ModelModal.vue';
import CustomFilters from '@/components/CustomFilters.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import Pagination from '@/components/Pagination.vue';
import GenericCard from '@/components/GenericCard.vue';
import GenericTable from '@/components/GenericTable.vue';

const resourceApi = 'item-categories';
const resourceName = 'Categorias';

const orderOptions = [
    { text: 'Fecha de modificación', value: 'updated_at' },
    { text: 'Fecha de creación', value: 'created_at' },
    { text: 'Nombre', value: 'name' }
];

const headers = ref([
    { text: 'Nombre', value: 'name' },
    { text: 'Suscripción', value: 'subscription' },
    { text: 'Activo', value: 'active' },
    { text: 'Creación', value: 'created_at' }
]);

const showModal = ref(false);
const isEditMode = ref(false);
const entityData = ref({
    name: '',
    description: ''
});
const viewAsCard = ref(true);
const showDeleted = ref(false);
const loading = ref(true);
const displayAsForm = ref(true);
const keepFormOpen = ref(true);
const isFormVisible = ref(false);

const {
    openCreateModal,
    openEditModal,
    handleEntityUpdated,
    applyFilter,
    updateItemsPerPage,
    updateViewAsCard,
    updateShowDeleted,
    restoreItem,
    deleteItem,
    fetchItems,
    changePage,
    filteredItems,
    totalFilteredItems,
    currentPage,
    itemsPerPage,
    lastPage
} = useModelActions(resourceApi, {
    showModal,
    isEditMode,
    entityData,
    viewAsCard,
    showDeleted,
    displayAsForm,
    loading,
    isFormVisible,
    keepFormOpen,
    resourceName
});

watch([itemsPerPage, currentPage], ([newItemsPerPage, newPage]) => {
    if (newItemsPerPage || newPage) {
        fetchItems(newPage);
    }
});

watch(showModal, async (newValue) => {
    if (newValue) {
        await nextTick();
        initializeChoices("choices-type");
    }
});

const initializeChoices = async (elementId) => {
    await nextTick(); // Espera a que el DOM esté listo
    const element = document.getElementById(elementId);
    if (element && !element.classList.contains('choices__input')) {
        new Choices(element, { allowHTML: true });
    }
};

onMounted(async () => {
    await fetchItems();
});

const closeForm = () => {
    isFormVisible.value = false;
    fetchItems(currentPage.value);
};

</script>

<style scoped>
.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}
</style>
